import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useRuleDetail } from '../hooks/useDqs';
import { StatusBadge } from './StatusBadge';
import type { CandidateRule } from '../dqs.api';
import { LAGEN } from '@xxtract/ui';
import { cn } from '@/lib/utils';

interface RuleDetailDialogProps {
  ruleId: string | null;
  onClose: () => void;
}

function formatDate(dateStr: string | null, language: string): string {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleDateString(language, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
}

function OverrideReasons({ rule }: { rule: CandidateRule }) {
  const { t, i18n } = useTranslation();
  const reasons = useMemo(() => {
    if (rule.overrideHistory?.length) {
      return rule.overrideHistory;
    }
    const fallback: Array<{ reason: string; type?: string; timestamp: string | null }> = [];
    if (rule.degradedReason) {
      fallback.push({ reason: rule.degradedReason, type: 'degraded', timestamp: rule.degradedAt });
    }
    if (rule.disabledReason) {
      fallback.push({ reason: rule.disabledReason, type: 'disabled', timestamp: rule.disabledAt });
    }
    return fallback;
  }, [rule]);

  if (reasons.length === 0) {
    return (
      <p
        className="text-[0.8125rem]"
        style={{ color: 'var(--foreground-muted)' }}
        data-testid="no-override-reasons"
      >
        {t('dqs.detail.noOverrideReasons')}
      </p>
    );
  }

  return (
    <ul className="space-y-2" data-testid="override-reasons-list">
      {reasons.map((override, idx) => (
        <li
          key={idx}
          className="rounded-lg p-3 text-[0.8125rem]"
          style={{ backgroundColor: 'var(--background-muted)', border: '1px solid var(--border-soft)' }}
        >
          <div className="flex items-start justify-between">
            <span>{override.reason}</span>
            {override.timestamp && (
              <span className="text-xs" style={{ color: 'var(--foreground-muted)' }}>
                {formatDate(override.timestamp, i18n.language)}
              </span>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}

function AcceptanceRateBar({ rule }: { rule: CandidateRule }) {
  const { t } = useTranslation();
  const percentage = (rule.acceptanceRate || 0) * 100;
  const total = rule.acceptCount + rule.rejectCount;

  const barColor =
    percentage >= 95 ? 'var(--success)' :
    percentage >= 80 ? 'var(--warning)' :
    'var(--destructive)';

  return (
    <div data-testid="acceptance-rate-section">
      {/* Acceptance rate bar */}
      <div
        className="mb-2 h-5 w-full overflow-hidden rounded-full"
        style={{ backgroundColor: 'var(--background-muted)' }}
      >
        <div
          className="flex h-full items-center justify-center rounded-full text-xs font-medium text-white"
          style={{
            width: `${percentage}%`,
            backgroundColor: barColor,
            minWidth: percentage > 0 ? '2rem' : '0',
          }}
          data-testid="acceptance-rate-bar"
        >
          {percentage.toFixed(1)}%
        </div>
      </div>

      {/* Stacked accept/reject bar */}
      {total > 0 && (
        <div className="mb-2 flex h-4 w-full overflow-hidden rounded-full">
          <div
            className="flex items-center justify-center text-[10px] font-medium text-white"
            style={{ width: `${(rule.acceptCount / total) * 100}%`, backgroundColor: 'var(--success-dark)' }}
          >
            {rule.acceptCount}
          </div>
          <div
            className="flex items-center justify-center text-[10px] font-medium text-white"
            style={{ width: `${(rule.rejectCount / total) * 100}%`, backgroundColor: 'var(--destructive)' }}
          >
            {rule.rejectCount}
          </div>
        </div>
      )}

      <p
        className="text-[0.8125rem]"
        style={{ color: 'var(--foreground-muted)' }}
        data-testid="acceptance-rate-text"
      >
        {t('dqs.detail.acceptanceRateSummary', { accepted: rule.acceptCount, rejected: rule.rejectCount, percentage: percentage.toFixed(1) })}
      </p>
    </div>
  );
}

function RuleDetailContent({ rule }: { rule: CandidateRule }) {
  const { t, i18n } = useTranslation();
  return (
    <div data-testid="rule-detail-content">
      {/* Prominent transformation */}
      <div
        className="mb-4 rounded-lg p-4"
        style={{
          backgroundColor: 'var(--background-muted)',
          borderLeft: '4px solid var(--primary)',
        }}
        data-testid="rule-transformation"
      >
        <div className="mb-1 text-xs" style={{ color: 'var(--foreground-muted)' }}>
          {t('dqs.detail.transformation')}
        </div>
        <div style={{ fontSize: '1.15rem' }}>
          <span style={{ color: 'var(--destructive)' }}>{rule.exampleFromValue}</span>
          <span className="mx-2" style={{ color: 'var(--foreground-muted)' }}>&rarr;</span>
          <span style={{ color: 'var(--success-dark)' }}>{rule.exampleToValue}</span>
        </div>
      </div>

      {/* Rule information grid */}
      <div className="mb-6 grid grid-cols-2 gap-x-6 gap-y-3 text-[0.8125rem]">
        <div>
          <dt className="font-medium" style={{ color: 'var(--foreground-muted)', fontSize: '0.75rem' }}>{t('dqs.detail.patternKey')}</dt>
          <dd className="mt-0.5 font-mono text-xs">{rule.patternKey}</dd>
        </div>
        <div>
          <dt className="font-medium" style={{ color: 'var(--foreground-muted)', fontSize: '0.75rem' }}>{t('dqs.detail.fieldName')}</dt>
          <dd className="mt-0.5">{rule.fieldName}</dd>
        </div>
        <div>
          <dt className="font-medium" style={{ color: 'var(--foreground-muted)', fontSize: '0.75rem' }}>{t('dqs.detail.type')}</dt>
          <dd className="mt-0.5">{t(rule.transformationType === 'formatting' ? 'dqs.transformation.formatting' : 'dqs.transformation.mapping')}</dd>
        </div>
        <div>
          <dt className="font-medium" style={{ color: 'var(--foreground-muted)', fontSize: '0.75rem' }}>{t('dqs.detail.layer')}</dt>
          <dd className="mt-0.5">{t(rule.layer === 1 ? 'dqs.detail.activeLayer' : 'dqs.detail.candidateLayer')}</dd>
        </div>
        <div>
          <dt className="font-medium" style={{ color: 'var(--foreground-muted)', fontSize: '0.75rem' }}>{t('dqs.detail.status')}</dt>
          <dd className="mt-0.5"><StatusBadge status={rule.status} /></dd>
        </div>
        <div>
          <dt className="font-medium" style={{ color: 'var(--foreground-muted)', fontSize: '0.75rem' }}>{t('dqs.detail.confidence')}</dt>
          <dd className="mt-0.5">{(rule.confidence * 100).toFixed(1)}%</dd>
        </div>
        <div>
          <dt className="font-medium" style={{ color: 'var(--foreground-muted)', fontSize: '0.75rem' }}>{t('dqs.detail.confirmations')}</dt>
          <dd className="mt-0.5">{rule.confirmationCount}</dd>
        </div>
        <div>
          <dt className="font-medium" style={{ color: 'var(--foreground-muted)', fontSize: '0.75rem' }}>{t('dqs.detail.uniqueEmployees')}</dt>
          <dd className="mt-0.5">{rule.uniqueEmployeeCount}</dd>
        </div>
        <div>
          <dt className="font-medium" style={{ color: 'var(--foreground-muted)', fontSize: '0.75rem' }}>{t('dqs.detail.totalObservations')}</dt>
          <dd className="mt-0.5">{rule.totalObservations}</dd>
        </div>
        <div>
          <dt className="font-medium" style={{ color: 'var(--foreground-muted)', fontSize: '0.75rem' }}>{t('dqs.detail.autoCorrection')}</dt>
          <dd className="mt-0.5">{t(rule.autoCorrectionEnabled ? 'dqs.autoCorrection.enabled' : 'dqs.autoCorrection.disabled')}</dd>
        </div>
      </div>

      {/* Timeline */}
      <div className="mb-6">
        <h4
          className="mb-2 text-[0.8125rem] font-semibold"
          style={{ color: 'var(--foreground-heading)' }}
        >
          {t('dqs.detail.timeline')}
        </h4>
        <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-[0.8125rem]">
          <div>
            <dt className="font-medium" style={{ color: 'var(--foreground-muted)', fontSize: '0.75rem' }}>{t('dqs.detail.firstDetected')}</dt>
            <dd className="mt-0.5">{formatDate(rule.firstDetectedAt, i18n.language)}</dd>
          </div>
          <div>
            <dt className="font-medium" style={{ color: 'var(--foreground-muted)', fontSize: '0.75rem' }}>{t('dqs.detail.lastDetected')}</dt>
            <dd className="mt-0.5">{formatDate(rule.lastDetectedAt, i18n.language)}</dd>
          </div>
          {rule.promotedAt && (
            <div>
              <dt className="font-medium" style={{ color: 'var(--foreground-muted)', fontSize: '0.75rem' }}>{t('dqs.detail.promotedAt')}</dt>
              <dd className="mt-0.5">{formatDate(rule.promotedAt, i18n.language)}</dd>
            </div>
          )}
          {rule.degradedAt && (
            <div>
              <dt className="font-medium" style={{ color: 'var(--foreground-muted)', fontSize: '0.75rem' }}>{t('dqs.detail.degradedAt')}</dt>
              <dd className="mt-0.5">{formatDate(rule.degradedAt, i18n.language)}</dd>
            </div>
          )}
          {rule.disabledAt && (
            <div>
              <dt className="font-medium" style={{ color: 'var(--foreground-muted)', fontSize: '0.75rem' }}>{t('dqs.detail.disabledAt')}</dt>
              <dd className="mt-0.5">{formatDate(rule.disabledAt, i18n.language)}</dd>
            </div>
          )}
        </div>
      </div>

      {/* Acceptance rate */}
      <div className="mb-6">
        <h4
          className="mb-2 text-[0.8125rem] font-semibold"
          style={{ color: 'var(--foreground-heading)' }}
        >
          {t('dqs.detail.acceptanceRate')}
        </h4>
        <AcceptanceRateBar rule={rule} />
      </div>

      {/* Override reasons */}
      <div>
        <h4
          className="mb-2 text-[0.8125rem] font-semibold"
          style={{ color: 'var(--foreground-heading)' }}
        >
          {t('dqs.detail.overrideReasons')}
        </h4>
        <OverrideReasons rule={rule} />
      </div>
    </div>
  );
}

export function RuleDetailDialog({ ruleId, onClose }: RuleDetailDialogProps) {
  const { t } = useTranslation();
  const { data, isLoading, error } = useRuleDetail(ruleId);

  // Close on ESC key
  useEffect(() => {
    if (!ruleId) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [ruleId, onClose]);

  if (!ruleId) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn('fixed inset-0 bg-black/30', LAGEN.venster)}
        onClick={onClose}
        data-testid="rule-detail-backdrop"
      />
      {/* Dialog */}
      <div
        className={cn('fixed left-1/2 top-1/2 max-h-[85vh] w-full max-w-2xl -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-xl', LAGEN.venster)}
        style={{ backgroundColor: 'var(--background)', boxShadow: 'var(--card-shadow-md)' }}
        data-testid="rule-detail-dialog"
      >
        {/* Header */}
        <div
          className="sticky top-0 flex items-center justify-between px-6 py-4"
          style={{
            borderBottom: '1px solid var(--border)',
            backgroundColor: 'var(--background)',
          }}
        >
          <h2
            className="text-base font-semibold"
            style={{ color: 'var(--foreground-heading)' }}
          >
            {t('dqs.detail.title', { fieldName: data?.data?.fieldName ?? t('common.loading') })}
          </h2>
          <button
            onClick={onClose}
            className="rounded-lg p-1"
            style={{ color: 'var(--foreground-muted)' }}
            data-testid="rule-detail-close-x"
            aria-label={t('common.close')}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>close</span>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          {isLoading && (
            <div className="flex items-center justify-center py-12" data-testid="rule-detail-loading">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-[var(--border)] border-t-[var(--primary)]" />
            </div>
          )}
          {error && (
            <div
              className="rounded-lg p-4 text-center text-[0.8125rem]"
              style={{ backgroundColor: 'var(--background-muted)', color: 'var(--foreground-muted)' }}
              data-testid="rule-detail-error"
            >
              {t('dqs.detail.unavailable')}
            </div>
          )}
          {data && <RuleDetailContent rule={data.data} />}
        </div>

        {/* Footer */}
        <div
          className="sticky bottom-0 flex justify-end px-6 py-4"
          style={{ borderTop: '1px solid var(--border)', backgroundColor: 'var(--background)' }}
        >
          <button
            onClick={onClose}
            className="rounded-lg px-4 py-2 text-[0.8125rem] font-medium"
            style={{ border: '1px solid var(--border)', color: 'var(--foreground)' }}
            data-testid="rule-detail-close"
          >
            {t('common.close')}
          </button>
        </div>
      </div>
    </>
  );
}
